import { z } from 'zod';
import { parseLlmJson } from '../llm/parse.js';
import { invalidEffortEntry, invalidProviderEntry, isClaudeEffort, parsePositiveInteger, resolveLlmProvider, } from '../llm/provider.js';
import { renderReportFacts, toReportFacts, } from './figures.js';
/** Strict on purpose: model output is untrusted input, not persisted data. */
export const ReportNarrativeSchema = z.strictObject({
    headline: z.string().min(1).max(400),
    sections: z
        .array(z.strictObject({
        title: z.string().min(1).max(120),
        body: z.string().min(1).max(4000),
        /** Fact ids the section draws on. Every one is checked against the sheet we sent. */
        figureIds: z.array(z.string().min(1).max(80)).min(1).max(24),
    }))
        .min(1)
        .max(8),
    /** Things a human should look at. Prose, no figures required. */
    watchItems: z.array(z.string().min(1).max(600)).max(10),
});
/**
 * Enough room for eight sections of prose plus the model's own reasoning, which is billed
 * against the same ceiling. A ceiling too low does not produce a shorter report — it produces
 * `truncated`, having paid for the thinking.
 */
export const DEFAULT_REPORT_MAX_TOKENS = 8_000;
/** Claude's adaptive thinking cannot be disabled at `xhigh`, so the ceiling is sized for it. */
export const DEFAULT_CLAUDE_REPORT_MAX_TOKENS = 32_000;
export function readReporterConfig(env) {
    const provider = resolveLlmProvider(env);
    if (!provider.ok)
        return { ok: false, missing: [invalidProviderEntry(provider.value)] };
    if (provider.provider === 'anthropic') {
        const model = env.CLAUDE_REPORT_MODEL?.trim();
        if (!model)
            return { ok: false, missing: ['CLAUDE_REPORT_MODEL'] };
        const effort = env.CLAUDE_REPORT_EFFORT?.trim();
        if (effort && !isClaudeEffort(effort)) {
            return { ok: false, missing: [invalidEffortEntry('CLAUDE_REPORT_EFFORT', effort)] };
        }
        return {
            ok: true,
            config: {
                model,
                maxTokens: parsePositiveInteger(env.CLAUDE_REPORT_MAX_TOKENS, DEFAULT_CLAUDE_REPORT_MAX_TOKENS),
                effort: effort ? effort : null,
            },
        };
    }
    const model = env.OPENAI_REPORT_MODEL?.trim();
    if (!model)
        return { ok: false, missing: ['OPENAI_REPORT_MODEL'] };
    const maxTokens = parsePositiveInteger(env.OPENAI_REPORT_MAX_TOKENS, DEFAULT_REPORT_MAX_TOKENS);
    const effort = env.OPENAI_REPORT_EFFORT?.trim();
    return { ok: true, config: { model, maxTokens, effort: effort ? effort : null } };
}
/** Exactly one retry, and only for a mangled JSON envelope. */
export const MAX_REPORT_ATTEMPTS = 2;
const SYSTEM_PROMPT = [
    'You write short internal delivery reports for the staff of a software maintenance retainer.',
    'The audience is internal: the client never sees this. Be direct about overruns and estimation drift.',
    'You are given a fact sheet of figures that were computed from the database.',
    'RULES, in order of importance:',
    '1. Never state a number that is not in the fact sheet. Do not add, average, round, convert, or compute percentages. If a figure you want does not exist, describe the situation in words instead.',
    '2. Copy figures character for character as they appear in the fact sheet.',
    '3. Every section must list the fact ids it draws on in figureIds, and those ids must come from the fact sheet.',
    '4. Distinguish window figures from all-time figures. Do not describe an all-time total as if it happened in the window.',
    '5. Say plainly when the regression has too small a sample or no explanatory power. Do not present a slope as a forecast when it does not support one.',
    'Reply only with the JSON object described below and nothing else.',
].join('\n');
const OUTPUT_CONTRACT = [
    'Return JSON of exactly this shape:',
    '{"headline": string, "sections": [{"title": string, "body": string, "figureIds": [string, ...]}, ...], "watchItems": [string, ...]}',
    'Cover, in this order and only where the facts support it: what was delivered in the window;',
    'estimated versus actual hours and where the variance concentrated; which issues absorbed',
    'disproportionate effort; and what the token-usage-to-hours regression currently says.',
].join('\n');
export function buildReportRequest(figures, facts, config) {
    const scope = figures.projectName === null
        ? 'Scope: the whole organization.'
        : `Scope: the project "${figures.projectName}" only.`;
    const parts = [scope];
    if (figures.outlierScanTruncated) {
        parts.push('Note: the delivered-issue scan hit its page budget, so the effort outliers are drawn from the most recently updated issues rather than every one.');
    }
    parts.push('', 'FACT SHEET', renderReportFacts(facts), '', OUTPUT_CONTRACT);
    return {
        model: config.model,
        maxTokens: config.maxTokens,
        effort: config.effort,
        system: SYSTEM_PROMPT,
        userContent: parts.join('\n'),
    };
}
/**
 * Every number-shaped token in a piece of text, normalised so `1,234` and `1234` compare
 * equal. Deliberately crude: it over-collects (dates and issue numbers come out as digits
 * too), and over-collecting is the safe direction — a figure the sheet contains is allowed,
 * and everything else is refused.
 */
export function extractNumericTokens(text) {
    const matches = text.match(/\d+(?:[.,]\d+)*/g) ?? [];
    return matches.map((token) => token.replace(/,/g, ''));
}
/**
 * The fabrication check.
 *
 * The allow-list is built by tokenising the FACT SHEET ITSELF rather than a curated list of
 * values, so anything we showed the model it may repeat, and nothing else. A derived figure —
 * a percentage, an average, a total the model added up — is by construction absent from the
 * sheet, and that is exactly the class of number this exists to catch.
 */
export function verifyFiguresGrounded(narrative, facts) {
    const allowedNumbers = new Set(extractNumericTokens(renderReportFacts(facts)));
    const allowedIds = new Set(facts.map((f) => f.id));
    const prose = [
        narrative.headline,
        ...narrative.sections.flatMap((section) => [section.title, section.body]),
        ...narrative.watchItems,
    ].join('\n');
    const fabricated = [...new Set(extractNumericTokens(prose))].filter((token) => !allowedNumbers.has(token));
    const unknownFigureIds = [
        ...new Set(narrative.sections.flatMap((section) => section.figureIds)),
    ].filter((id) => !allowedIds.has(id));
    return {
        ok: fabricated.length === 0 && unknownFigureIds.length === 0,
        fabricated,
        unknownFigureIds,
    };
}
function failed(reason, detail, attempts) {
    return { status: 'failed', reason, detail, attempts };
}
function errorMessage(error) {
    if (error instanceof Error)
        return error.message;
    return typeof error === 'string' ? error : 'unknown provider error';
}
function isTimeout(error) {
    if (!(error instanceof Error))
        return false;
    if (error.name === 'AbortError' || error.name === 'TimeoutError')
        return true;
    if ('code' in error && typeof error.code === 'string' && /TIMEOUT|TIMEDOUT/i.test(error.code)) {
        return true;
    }
    return /timed?\s?out/i.test(error.message);
}
function interpret(response, facts, attempts) {
    const text = response.outputText?.trim() ?? '';
    // Checked before the empty case: a refusal and a truncation are both billed responses with
    // a specific cause, and reporting either as "the model said nothing" loses the cause.
    if (response.stopReason === 'refusal') {
        return {
            retryable: false,
            outcome: failed('refused', `category=${response.refusalCategory ?? 'unspecified'}`, attempts),
        };
    }
    if (response.stopReason === 'max_tokens' ||
        response.stopReason === 'model_context_window_exceeded') {
        return {
            retryable: false,
            outcome: failed('truncated', `stop_reason=${response.stopReason} after ${response.outputTokens ?? 0} output tokens`, attempts),
        };
    }
    if (text.length === 0) {
        return {
            retryable: false,
            outcome: failed('empty_response', `stop_reason=${response.stopReason ?? 'unknown'}`, attempts),
        };
    }
    const parsed = parseLlmJson(text);
    if (!parsed.ok) {
        // The only retryable failure: the model had something to say and mangled the envelope.
        return {
            retryable: true,
            outcome: failed('unparseable_output', `${parsed.reason}: ${text.slice(0, 200)}`, attempts),
        };
    }
    const answer = ReportNarrativeSchema.safeParse(parsed.value);
    if (!answer.success) {
        const first = answer.error.issues[0];
        return {
            retryable: false,
            outcome: failed('unexpected_answer', first ? `${first.path.join('.') || '(root)'}: ${first.message}` : 'schema mismatch', attempts),
        };
    }
    const verification = verifyFiguresGrounded(answer.data, facts);
    if (!verification.ok) {
        // Fail closed. A report with one invented figure is not partially usable: the reader has
        // no way to tell which line was the invented one.
        return {
            retryable: false,
            outcome: failed(verification.fabricated.length > 0 ? 'unverified_figures' : 'unexpected_answer', verification.fabricated.length > 0
                ? `numbers absent from the fact sheet: ${verification.fabricated.slice(0, 10).join(', ')}`
                : `unknown figure ids: ${verification.unknownFigureIds.slice(0, 10).join(', ')}`, attempts),
        };
    }
    return { retryable: false, outcome: { status: 'ok', narrative: answer.data, attempts } };
}
export async function generateReportNarrative(figures, deps) {
    const config = readReporterConfig(deps.env);
    if (!config.ok) {
        return failed('misconfigured', `missing ${config.missing.join(', ')}`, 0);
    }
    const facts = toReportFacts(figures);
    const request = buildReportRequest(figures, facts, config.config);
    let attempts = 0;
    let lastFailure;
    while (attempts < MAX_REPORT_ATTEMPTS) {
        attempts += 1;
        let response;
        try {
            response = await deps.provider.complete(request);
        }
        catch (error) {
            // Reported apart because they read very differently in an incident, and handed back to
            // the caller rather than retried here: the queue owns the backoff for these two.
            const reason = isTimeout(error) ? 'timeout' : 'transport_error';
            return failed(reason, errorMessage(error), attempts);
        }
        const verdict = interpret(response, facts, attempts);
        if (!verdict.retryable)
            return verdict.outcome;
        lastFailure = verdict.outcome;
    }
    return lastFailure ?? failed('unparseable_output', 'no usable response', attempts);
}
//# sourceMappingURL=report.js.map